/* ══ F15 STEP 3.2 / 3.7 — THE WORKOUT'S SWITCH AND EACH ROW'S RULE, THE SERVER'S TWIN OF `CoachWorkoutRules.swift` ════════
 * THE HOME: `coach_workout_engine` (the switch + `deload_every`, one row per ROOT workout, no row = ON, never) and
 * `coach_workout_rules` (one row per (ROOT, library exercise); NULL config = "does not progress here"; no row = the library's
 * `progression_config`). The ROOT is `coalesce(root_workout_id, id)`.
 *
 * ⚠️ THE ONE MERGE, THREE TIMES, PINNED BY ONE CORPUS: `CoachWorkoutRules.effective` (Swift, gate
 * `f15-3-2-the-phone-obeys-the-workout`), `effectiveRule` in `web/src/lib/workoutEngine.ts`, and this. All three are run over
 * `web/src/lib/__fixtures__/workoutRuleCorpus.json`; `sharedWorkoutRules.test.ts` also asserts this one and the web's agree on
 * every case. `_shared` may not import from `web/`, so the merge is stated here and the corpus holds the two together.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

import { isLadder, ladderJson, ladderRest } from "./coachLadder.ts";
import { isTabs, tabsJson, tabsRest } from "./coachTabs.ts";

/** `CoachWorkoutRules.progressDefault` — the switch with no row: ON. */
export const PROGRESS_DEFAULT = true;

/** `CoachWorkoutRules.Engine` — one ROOT workout's settings. `rules` keys are lower-cased library ids; a `null` value is the
 *  row's NULL config ("does not progress here"). `deloadEvery` null = never. */
export type WorkoutRulesEngine = {
  progress: boolean;
  rules: Map<string, unknown | null>;
  deloadEvery: number | null;
};

/** `CoachWorkoutRules.Source` — the raw values are the web's `RuleSource`. */
export type RuleSource = "off" | "workout" | "library";

/** `CoachWorkoutRules.effective` — THE merge. Switch OFF → nothing; a workout row → its config (NULL = does not progress
 *  here); an exercise a ladder of this workout moved the row to → the rest of that chain (empty = the chain's end), ladders
 *  read in SORTED lower-cased key order; none → the library's. */
export function effectiveRule(engine: Pick<WorkoutRulesEngine, "progress" | "rules">, exerciseId: string,
                              libraryConfig: unknown): { config: unknown | null; source: RuleSource } {
  if (!engine.progress) return { config: null, source: "off" };
  if (engine.rules.has(exerciseId)) {
    /* `WorkoutRule(_:)`: absent or JSON `null` both mean NULL — `.doesNotProgress`. */
    const rule = engine.rules.get(exerciseId);
    return { config: rule === undefined || rule === null ? null : rule, source: "workout" };
  }
  const id = exerciseId.toLowerCase();
  /* F15 step 4.1 — ladders and tabs in ONE sorted key order (`CoachWorkoutRules.effective`'s `chains`). */
  const chains = [...engine.rules.entries()]
    .filter(([, rule]) => rule !== undefined && rule !== null && (isLadder(rule) || isTabs(rule)))
    .map(([key, rule]) => [key.toLowerCase(), rule] as const)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  for (const [, config] of chains) {
    const rest = ladderRest(config, id);
    if (rest !== null) return { config: ladderJson(rest), source: "workout" };
    const tabRest = tabsRest(config, id);
    if (tabRest !== null) return { config: tabsJson(tabRest), source: "workout" };
  }
  return { config: libraryConfig === undefined || libraryConfig === null ? null : libraryConfig, source: "library" };
}
